import type { Layout, NarrativePoint, SubjectPoint, ThreadPoint } from './layout'
import { narrativeColour, rgba, threatColour, type Palette, type Rgb } from './palette'
import { levelAlpha, px, py, scaleRadius, zoomOf, type Viewport } from './viewport'

/**
 * The canvas renderer.
 *
 * ## One level is drawn at a time, and that is the performance story
 *
 * At any scale exactly one of subject / narrative / thread is fully present and at most one
 * other is fading. Everything is culled to the viewport before it is drawn. So the number of
 * primitives per frame is bounded by what actually fits on the screen rather than by the size
 * of the corpus: a window holding four thousand threads costs the same per frame as one
 * holding forty, because at the scale where four thousand threads would all be visible, they
 * are drawn as thirty subjects instead.
 *
 * ## Nothing here encodes a quantity with hue alone
 *
 * Threat is on the app's severity ramp AND on radius. Coordination is a hue, a dashed ring and
 * a label. Volume is area. Every reading survives greyscale and colour-blind vision, which is
 * the same rule the rest of the app's charts follow — see `shared/ui/charts.tsx`.
 */

const TAU = Math.PI * 2

/**
 * Label collision.
 *
 * ## Why a map cannot just draw every label
 *
 * At the narrative level a subject's clusters sit within a few pixels of each other, and their
 * labels are twenty times wider than their nodes. Drawing them all produces a stack of
 * overlapping text that is not merely ugly — it is unreadable, which is worse than a label that
 * was never drawn, because the reader cannot tell which words belong to which node.
 *
 * So labels are placed the way a cartographer places them: candidates are sorted by importance,
 * each claims a rectangle, and one that cannot claim its space is dropped. Importance is
 * selection first, then hover, then size — so the thing the reader is looking at always keeps
 * its name, and what gets dropped is the small cluster they have not asked about.
 */
class LabelGuard {
  private readonly boxes: number[][] = []

  /** Claim a box centred on (cx, top). Returns false when it would collide. */
  claim(cx: number, top: number, width: number, height: number): boolean {
    const x1 = cx - width / 2
    const y1 = top
    const x2 = cx + width / 2
    const y2 = top + height
    for (const b of this.boxes) {
      if (x1 < b[2]! && x2 > b[0]! && y1 < b[3]! && y2 > b[1]!) return false
    }
    this.boxes.push([x1, y1, x2, y2])
    return true
  }
}

/** Importance for label placement — selection beats hover beats size. */
function labelRank(selected: boolean, hovered: boolean, radius: number): number {
  return (selected ? 1e6 : 0) + (hovered ? 1e5 : 0) + radius
}

export interface Hover {
  kind: 'subject' | 'narrative' | 'thread' | 'platform'
  id: string
  /** Screen coordinates, for the tooltip. */
  sx: number
  sy: number
}

export interface Selection {
  kind: 'subject' | 'narrative' | 'thread'
  id: string
}

/**
 * What the map's colour and size are encoding.
 *
 * The three are genuinely different questions, and a board that could only answer one would
 * hide the other two. THREAT asks "what is dangerous"; COORDINATION asks "what is
 * manufactured"; REACH asks "what is actually being seen". A thread can score high on one and
 * low on the others — the fuel-queue rumour that is everywhere and harmless, the eleven-account
 * campaign nobody read — and collapsing them onto one scale is how a room answers the wrong
 * question.
 *
 * REACH drops hue entirely and encodes with size on a single accent, which is the rule the rest
 * of the app's charts follow: a magnitude is a length, never a colour.
 */
export type EncodeMode = 'THREAT' | 'COORDINATION' | 'REACH'

export interface Scene {
  layout: Layout
  palette: Palette
  viewport: Viewport
  width: number
  height: number
  dpr: number
  /** Milliseconds since the scene started — drives the sweep and the pulses. */
  now: number
  paused: boolean
  showLabels: boolean
  showFlow: boolean
  hidden: Set<string>
  hovered: Hover | null
  selected: Selection | null
  /** Threads matching the reader's search, when one is active. Empty set = no search. */
  matches: Set<string> | null
  encode: EncodeMode
  /** Largest engagement count in the payload — the denominator for REACH sizing. */
  reachMax: number
  /**
   * The room's deploy threshold, on the 1..10 threat scale.
   *
   * Carried into the scene rather than assumed, because every band on this canvas is derived
   * from it — see `threatBand` in `palette.ts`.
   */
  threshold: number
}

// ── encoding ─────────────────────────────────────────────────────────────────

function subjectInk(scene: Scene, node: SubjectPoint['node']) {
  const P = scene.palette
  if (scene.encode === 'REACH') return P.accent
  if (scene.encode === 'COORDINATION') return P.channel
  return threatColour(P, node.threatMax, scene.threshold)
}

function narrativeInk(scene: Scene, node: NarrativePoint['node']) {
  const P = scene.palette
  if (scene.encode === 'REACH') return P.accent
  if (scene.encode === 'COORDINATION') {
    return node.kind === 'COORDINATED' ? P.coordinated : P.unread
  }
  return narrativeColour(P, node.kind, node.threatMax, scene.threshold)
}

function threadInk(scene: Scene, node: ThreadPoint['node'], parentCoordinated: boolean) {
  const P = scene.palette
  if (scene.encode === 'REACH') return P.accent
  if (scene.encode === 'COORDINATION') return parentCoordinated ? P.coordinated : P.unread
  return threatColour(P, node.threat, scene.threshold)
}

/**
 * Radius under REACH.
 *
 * ## Square-rooted, so AREA carries the number
 *
 * A linear radius makes a thread with four times the engagement look sixteen times as big,
 * which is the classic way a bubble chart lies.
 *
 * ## It shrinks the quiet, it never grows the loud
 *
 * The multiplier tops out at exactly 1. The layout guarantees its nodes do not overlap at their
 * laid-out size, and that guarantee is computed once, at build time — so a render-time
 * multiplier above 1 silently breaks it. The first version scaled up to 2.05x and the two
 * biggest subjects promptly swallowed each other and the channel ring. Shrinking encodes the
 * same comparison and cannot violate a spacing the layout already proved.
 */
function reachScale(scene: Scene, engagements: number | null | undefined) {
  if (scene.encode !== 'REACH') return 1
  const share = Math.min(1, (engagements ?? 0) / Math.max(1, scene.reachMax))
  return 0.35 + Math.sqrt(share) * 0.65
}

interface Packet { subjectId: string, platformCode: string, t: number, speed: number }

/**
 * Flow particles, one small set per subject-platform pair.
 *
 * These are the only genuinely decorative thing on the canvas, and they earn their place by
 * showing DIRECTION — which channels are feeding which subject — at the one zoom level where
 * there is no room to write it down. They are capped hard: past a few hundred they stop being
 * legible as flow and become noise with a frame cost.
 */
export function buildPackets(layout: Layout, hidden: Set<string>): Packet[] {
  const out: Packet[] = []
  for (const s of layout.subjects) {
    for (const code of s.node.platforms) {
      if (hidden.has(code)) continue
      const count = 1 + Math.min(3, Math.round(s.node.velocity / 12))
      for (let i = 0; i < count; i++) {
        out.push({
          subjectId: s.node.id,
          platformCode: code,
          t: (i + 1) / (count + 1),
          speed: 0.06 + Math.min(0.22, s.node.velocity / 260),
        })
      }
      if (out.length > 420) return out
    }
  }
  return out
}

export function draw(ctx: CanvasRenderingContext2D, scene: Scene, packets: Packet[], dt: number) {
  const { layout: L, palette: P, viewport: V, width: W, height: H, dpr } = scene
  const alpha = levelAlpha(zoomOf(V))

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, W, H)

  const guard = new LabelGuard()

  drawStage(ctx, scene)
  drawRing(ctx, scene)
  drawPlatforms(ctx, scene)
  if (scene.showFlow && alpha.SUBJECT > 0.05) {
    drawFlow(ctx, scene, packets, dt, alpha.SUBJECT)
  }

  // Draw in ascending detail so a fading level sits under the one replacing it.
  if (alpha.SUBJECT > 0.01) drawSubjects(ctx, scene, alpha.SUBJECT, guard)
  if (alpha.NARRATIVE > 0.01) drawNarratives(ctx, scene, alpha.NARRATIVE, guard)
  if (alpha.THREAD > 0.01) drawThreads(ctx, scene, alpha.THREAD, guard)

  drawMinimap(ctx, scene)
  void L
  void P
}

// ── stage ────────────────────────────────────────────────────────────────────

function drawStage(ctx: CanvasRenderingContext2D, scene: Scene) {
  const { palette: P, width: W, height: H, layout: L, viewport: V } = scene
  const g = ctx.createRadialGradient(
    px(V, L.cx), py(V, L.cy), 0,
    px(V, L.cx), py(V, L.cy), Math.max(W, H) * 0.9,
  )
  g.addColorStop(0, rgba(P.stageEdge, 1))
  g.addColorStop(0.55, rgba(P.stage, 1))
  g.addColorStop(1, rgba(P.stage, 1))
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // A faint hex field inside the ring, drawn in world space so it scales with the map and
  // gives the eye something to measure motion against while panning.
  const k = V.k
  if (zoomOf(V) > 0.5) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(px(V, L.cx), py(V, L.cy), L.ringR * k, 0, TAU)
    ctx.clip()
    ctx.strokeStyle = rgba(P.line, 0.30)
    ctx.lineWidth = 0.6
    const size = 30 * k
    const w = size * Math.sqrt(3)
    const x0 = px(V, L.cx - L.ringR)
    const y0 = py(V, L.cy - L.ringR)
    const cols = Math.ceil((L.ringR * 2 * k) / w) + 2
    const rows = Math.ceil((L.ringR * 2 * k) / (size * 1.5)) + 2
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const ox = x0 + col * w + (row % 2 ? w / 2 : 0)
        const oy = y0 + row * size * 1.5
        ctx.beginPath()
        for (let q = 0; q < 6; q++) {
          const a = (Math.PI / 180) * (60 * q - 30)
          const hx = ox + size * Math.cos(a)
          const hy = oy + size * Math.sin(a)
          if (q) ctx.lineTo(hx, hy)
          else ctx.moveTo(hx, hy)
        }
        ctx.closePath()
        ctx.stroke()
      }
    }
    ctx.restore()
  }

  drawCore(ctx, scene)
}

/**
 * The core.
 *
 * A slow drift of particles at the centre of the ring, one per thread in the window, sampled.
 * It is the only thing on the board that reads at a glance as VOLUME rather than as structure —
 * a quiet window has a thin core and a loud one is dense — and it does that without a number,
 * which is what makes it useful behind everything else rather than competing with it.
 */
function drawCore(ctx: CanvasRenderingContext2D, scene: Scene) {
  const { palette: P, layout: L, viewport: V, now, paused } = scene
  const coreR = L.ringR * 0.30 * V.k
  if (coreR < 12) return
  const cx = px(V, L.cx)
  const cy = py(V, L.cy)
  const count = Math.min(520, 60 + scene.layout.subjects.reduce(
    (n, s) => n + s.node.threads, 0,
  ))
  const drift = paused ? 0 : now * 0.00006

  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < count; i++) {
    // Deterministic per index, so the core does not reshuffle between frames or between
    // visits — it should read as one object breathing, not as static.
    const seed = (i * 2654435761) >>> 0
    const rr = Math.pow((seed % 1000) / 1000, 0.55)
    const a = ((seed >> 10) % 6283) / 1000 + drift * (1.2 - rr) * (seed % 2 ? 1 : -1)
    const radius = rr * coreR * (0.86 + 0.14 * Math.sin(now * 0.0009 + i))
    ctx.fillStyle = rgba(P.accent, 0.06 + 0.20 * (1 - rr))
    ctx.fillRect(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius * 0.72, 1.4, 1.4)
  }
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * 1.5)
  glow.addColorStop(0, rgba(P.accent, 0.13))
  glow.addColorStop(0.4, rgba(P.accent, 0.035))
  glow.addColorStop(1, rgba(P.accent, 0))
  ctx.fillStyle = glow
  ctx.beginPath()
  ctx.arc(cx, cy, coreR * 1.5, 0, TAU)
  ctx.fill()
  ctx.restore()
}

function drawRing(ctx: CanvasRenderingContext2D, scene: Scene) {
  const { palette: P, layout: L, viewport: V, now, paused } = scene
  const scx = px(V, L.cx)
  const scy = py(V, L.cy)
  const r = L.ringR * V.k

  ctx.strokeStyle = rgba(P.accent, 0.20)
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.arc(scx, scy, r, 0, TAU)
  ctx.stroke()

  for (let i = 0; i < 180; i++) {
    const a = (i / 180) * TAU
    const long = i % 15 === 0
    ctx.strokeStyle = rgba(P.accent, long ? 0.34 : 0.13)
    ctx.lineWidth = long ? 1 : 0.6
    ctx.beginPath()
    ctx.moveTo(scx + Math.cos(a) * r * 1.012, scy + Math.sin(a) * r * 1.012)
    ctx.lineTo(scx + Math.cos(a) * r * (long ? 1.045 : 1.026),
      scy + Math.sin(a) * r * (long ? 1.045 : 1.026))
    ctx.stroke()
  }

  // The sweep says one thing only: this surface is live. It stops when the board is paused,
  // so a paused board cannot be mistaken for a moving one.
  if (!paused) {
    const sweep = (now / 9000) * TAU
    ctx.save()
    ctx.translate(scx, scy)
    ctx.rotate(sweep)
    const g = ctx.createLinearGradient(0, 0, r, 0)
    g.addColorStop(0, rgba(P.accent, 0))
    g.addColorStop(1, rgba(P.accent, 0.11))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.arc(0, 0, r, -0.3, 0)
    ctx.closePath()
    ctx.fill()
    ctx.strokeStyle = rgba(P.accent, 0.24)
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(r, 0)
    ctx.stroke()
    ctx.restore()
  }
}

function drawPlatforms(ctx: CanvasRenderingContext2D, scene: Scene) {
  const { palette: P, layout: L, viewport: V, hidden, hovered } = scene
  const k = zoomOf(V)
  for (const p of L.platforms) {
    const off = hidden.has(p.node.code)
    const sx = px(V, p.x)
    const sy = py(V, p.y)
    if (sx < -140 || sx > scene.width + 140 || sy < -80 || sy > scene.height + 80) continue
    const size = p.r * V.fitK * Math.pow(k, 0.5)
    const hot = hovered?.kind === 'platform' && hovered.id === p.node.code

    ctx.save()
    ctx.translate(sx, sy)
    ctx.globalAlpha = off ? 0.28 : 1
    ctx.beginPath()
    for (let q = 0; q < 6; q++) {
      const a = (Math.PI / 180) * (60 * q - 30)
      const hx = size * Math.cos(a)
      const hy = size * Math.sin(a)
      if (q) ctx.lineTo(hx, hy)
      else ctx.moveTo(hx, hy)
    }
    ctx.closePath()
    ctx.fillStyle = rgba(P.stage, 0.92)
    ctx.fill()
    ctx.strokeStyle = rgba(P.channel, hot ? 0.9 : 0.5)
    ctx.lineWidth = hot ? 1.6 : 1
    ctx.stroke()

    // A watch-only platform is drawn hollow. Half the platforms in this product have no write
    // API at all, and an operator reading a spike needs to know that before they plan a reply.
    if (p.node.canPost) {
      ctx.fillStyle = rgba(P.channel, 0.8)
      ctx.beginPath()
      ctx.arc(0, 0, 2, 0, TAU)
      ctx.fill()
    }

    const outward = Math.cos(p.angle)
    ctx.font = '300 9.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.textBaseline = 'middle'
    ctx.textAlign = outward > 0.25 ? 'left' : outward < -0.25 ? 'right' : 'center'
    ctx.fillStyle = rgba(P.channel, off ? 0.4 : 0.72)
    const lx = Math.cos(p.angle) * (size + 13)
    const ly = Math.sin(p.angle) * (size + 13)
    ctx.fillText(p.node.label.toUpperCase(), lx, ly)
    ctx.font = '300 8px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.fillStyle = rgba(P.inkFaint, off ? 0.5 : 0.9)
    ctx.fillText(
      off ? 'FILTERED OUT' : `${p.node.threads} ${p.node.canPost ? '' : '· WATCH ONLY'}`.trim(),
      lx, ly + 11,
    )
    ctx.restore()
  }
}

function drawFlow(
  ctx: CanvasRenderingContext2D, scene: Scene, packets: Packet[], dt: number, fade: number,
) {
  const { layout: L, viewport: V, paused, selected } = scene
  const scx = px(V, L.cx)
  const scy = py(V, L.cy)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (const pk of packets) {
    const subject = L.subjectById.get(pk.subjectId)
    const platform = L.platforms.find((p) => p.node.code === pk.platformCode)
    if (!subject || !platform) continue
    if (selected && selected.kind === 'subject' && selected.id !== pk.subjectId) continue
    if (!paused) {
      pk.t += pk.speed * dt
      if (pk.t > 1) pk.t -= 1
    }
    const ax = px(V, subject.x)
    const ay = py(V, subject.y)
    const bx = px(V, platform.x)
    const by = py(V, platform.y)
    const mx = (ax + bx) / 2
    const my = (ay + by) / 2
    const qx = mx + (mx - scx) * 0.10
    const qy = my + (my - scy) * 0.10
    const u = pk.t
    const iu = 1 - u
    const c = subjectInk(scene, subject.node)
    ctx.fillStyle = rgba(c, 0.55 * fade * Math.sin(u * Math.PI))
    ctx.beginPath()
    ctx.arc(
      iu * iu * ax + 2 * iu * u * qx + u * u * bx,
      iu * iu * ay + 2 * iu * u * qy + u * u * by,
      1.4 * Math.pow(zoomOf(V), 0.4), 0, TAU,
    )
    ctx.fill()
  }
  ctx.restore()
}

// ── level 0: subjects ────────────────────────────────────────────────────────

function drawSubjects(
  ctx: CanvasRenderingContext2D, scene: Scene, fade: number, guard: LabelGuard,
) {
  const { palette: P, layout: L, viewport: V, selected, hovered, showLabels } = scene
  const k = zoomOf(V)
  const pending: PendingLabel[] = []
  for (const s of L.subjects) {
    const sx = px(V, s.x)
    const sy = py(V, s.y)
    const R = scaleRadius(V, s.r) * reachScale(scene, s.node.engagements)
    if (sx + R < -80 || sx - R > scene.width + 80) continue
    if (sy + R < -80 || sy - R > scene.height + 80) continue

    const isSelected = selected?.kind === 'subject' && selected.id === s.node.id
    const isHovered = hovered?.kind === 'subject' && hovered.id === s.node.id
    const dimmed = selected && !isSelected ? 0.3 : 1
    const colour = subjectInk(scene, s.node)
    const pulse = 0.5 + 0.5 * Math.sin(scene.now / 900 + s.x * 0.01)

    ctx.save()
    ctx.globalAlpha = fade * dimmed

    // Halo. Its strength tracks velocity, so a subject accelerating is visible before any
    // label is legible — which is the only thing this level has to get right.
    ctx.globalCompositeOperation = 'lighter'
    const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, R * 2.4)
    const heat = Math.min(1, s.node.velocity / 60)
    halo.addColorStop(0, rgba(colour, 0.18 + heat * 0.22 * pulse))
    halo.addColorStop(0.5, rgba(colour, 0.05))
    halo.addColorStop(1, rgba(colour, 0))
    ctx.fillStyle = halo
    ctx.beginPath()
    ctx.arc(sx, sy, R * 2.4, 0, TAU)
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'

    ctx.fillStyle = rgba(P.stage, 0.86)
    ctx.beginPath()
    ctx.arc(sx, sy, R, 0, TAU)
    ctx.fill()
    ctx.strokeStyle = rgba(colour, 0.85)
    ctx.lineWidth = isSelected ? 2 : 1.2
    ctx.beginPath()
    ctx.arc(sx, sy, R, 0, TAU)
    ctx.stroke()

    // Hostile share as an arc on the rim: a part-to-whole that needs no legend and no second
    // colour, because the arc's LENGTH is the number.
    if (s.node.hostileShare > 0 && scene.encode === 'THREAT') {
      ctx.strokeStyle = rgba(P.threat[2], 0.9)
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(sx, sy, R, -Math.PI / 2, -Math.PI / 2 + TAU * s.node.hostileShare)
      ctx.stroke()
    }

    ctx.fillStyle = rgba(colour, 0.5 + 0.3 * pulse)
    ctx.beginPath()
    ctx.arc(sx, sy, Math.max(2, R * 0.16), 0, TAU)
    ctx.fill()

    if (isSelected || isHovered) {
      ctx.strokeStyle = rgba(P.accent, 0.7)
      ctx.lineWidth = 1
      ctx.setLineDash([2, 4])
      ctx.beginPath()
      ctx.arc(sx, sy, R + 8, 0, TAU)
      ctx.stroke()
      ctx.setLineDash([])
    }

    ctx.restore()

    if (showLabels && (R > 16 || isSelected || isHovered)) {
      pending.push({
        rank: labelRank(isSelected, isHovered, R),
        sx, y: sy + R + 9, alpha: fade * dimmed, colour, strong: isSelected || isHovered,
        lines: [
          clip(s.node.name ?? 'Unnamed subject', k >= 1.2 ? 44 : 26),
          `${s.node.threads} THREADS · ${s.node.narratives} NARRATIVES`,
          s.node.threatMax === null
            ? 'NOT SCORED'
            : `PEAK THREAT ${s.node.threatMax}/10 · ${Math.round(s.node.hostileShare * 100)}% HOSTILE`,
        ],
      })
    }
  }

  placeLabels(ctx, scene, guard, pending, 12)
}

interface PendingLabel {
  rank: number
  sx: number
  y: number
  alpha: number
  colour: Rgb
  strong: boolean
  lines: string[]
}

/**
 * Draw the labels that fit, most important first.
 *
 * The first line is the name and is measured for the claim; the rest ride along, because a
 * label whose name fits but whose metrics were dropped would read as a different, smaller fact
 * about the node rather than as a truncated one.
 */
function placeLabels(
  ctx: CanvasRenderingContext2D, scene: Scene, guard: LabelGuard,
  pending: PendingLabel[], titleSize: number,
) {
  const P = scene.palette
  pending.sort((a, b) => b.rank - a.rank)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  for (const label of pending) {
    ctx.font = `500 ${titleSize}px Inter, system-ui, sans-serif`
    const width = ctx.measureText(label.lines[0] ?? '').width + 10
    const height = 4 + label.lines.length * (titleSize + 2)
    if (!guard.claim(label.sx, label.y, width, height)) continue

    ctx.save()
    ctx.globalAlpha = label.alpha
    let y = label.y
    ctx.fillStyle = rgba(P.ink, label.strong ? 0.98 : 0.78)
    ctx.fillText(label.lines[0] ?? '', label.sx, y)
    y += titleSize + 3
    for (let i = 1; i < label.lines.length; i++) {
      ctx.font = '300 8.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
      ctx.fillStyle = i === label.lines.length - 1
        ? rgba(label.colour, 0.95)
        : rgba(P.inkDim, 0.85)
      ctx.fillText(label.lines[i] ?? '', label.sx, y)
      y += 12
    }
    ctx.restore()
  }
}

// ── level 1: narratives ──────────────────────────────────────────────────────

function drawNarratives(
  ctx: CanvasRenderingContext2D, scene: Scene, fade: number, guard: LabelGuard,
) {
  const { palette: P, layout: L, viewport: V, selected, hovered, showLabels } = scene
  const k = zoomOf(V)
  const pending: PendingLabel[] = []

  // The parent stays on screen as an outline and a pinned label. Losing the parent entirely on
  // the way down is what makes a zoomable graph disorienting — you descend and no longer know
  // whose narratives you are looking at.
  for (const s of L.subjects) {
    const sx = px(V, s.x)
    const sy = py(V, s.y)
    const R = scaleRadius(V, s.r)
    if (sx + R < -100 || sx - R > scene.width + 100) continue
    if (sy + R < -100 || sy - R > scene.height + 100) continue
    ctx.save()
    ctx.globalAlpha = fade
    ctx.strokeStyle = rgba(P.line, 0.8)
    ctx.setLineDash([3, 5])
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.arc(sx, sy, R, 0, TAU)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.font = '500 10px ui-monospace, "JetBrains Mono", Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.fillStyle = rgba(P.accent, 0.85)
    ctx.fillText((s.node.name ?? 'UNNAMED').toUpperCase(), sx, sy - R - 7)
    ctx.restore()
  }

  for (const s of L.subjects) {
    for (const n of s.narratives) {
      const sx = px(V, n.x)
      const sy = py(V, n.y)
      const R = Math.max(3, scaleRadius(V, n.r) * reachScale(scene, n.node.engagements))
      if (sx + R < -60 || sx - R > scene.width + 60) continue
      if (sy + R < -60 || sy - R > scene.height + 60) continue

      const isSelected = selected?.kind === 'narrative' && selected.id === n.node.id
      const isHovered = hovered?.kind === 'narrative' && hovered.id === n.node.id
      const colour = narrativeInk(scene, n.node)
      const coordinated = n.node.kind === 'COORDINATED'

      ctx.save()
      ctx.globalAlpha = fade * (selected && !isSelected ? 0.32 : 1)
      ctx.globalCompositeOperation = 'lighter'
      const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, R * 2.6)
      halo.addColorStop(0, rgba(colour, 0.24))
      halo.addColorStop(1, rgba(colour, 0))
      ctx.fillStyle = halo
      ctx.beginPath()
      ctx.arc(sx, sy, R * 2.6, 0, TAU)
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'

      ctx.fillStyle = rgba(P.stage, 0.9)
      ctx.beginPath()
      ctx.arc(sx, sy, R, 0, TAU)
      ctx.fill()
      // Coordinated narratives are dashed as well as differently hued, so the distinction does
      // not rest on colour alone.
      ctx.strokeStyle = rgba(colour, 0.9)
      ctx.lineWidth = isSelected ? 2 : 1.2
      if (coordinated) ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.arc(sx, sy, R, 0, TAU)
      ctx.stroke()
      ctx.setLineDash([])

      if (isSelected || isHovered) {
        ctx.strokeStyle = rgba(P.accent, 0.7)
        ctx.setLineDash([2, 4])
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(sx, sy, R + 7, 0, TAU)
        ctx.stroke()
        ctx.setLineDash([])
      }

      ctx.restore()

      if (showLabels && (R > 6 || isSelected || isHovered)) {
        const lines = [
          clip(
            coordinated ? n.node.label : `${n.node.label} · ${n.node.subjectName ?? 'unscoped'}`,
            k >= 3 ? 52 : 30,
          ),
          coordinated
            ? `COORDINATED · ${n.node.distinctAuthors} AUTHORS · ${n.node.threads} THREADS`
            : `${n.node.threads} THREADS · ${n.node.sentiment}`,
        ]
        if (k >= 3) {
          lines.push(n.node.threatMax === null
            ? 'NOT SCORED'
            : `PEAK ${n.node.threatMax}/10 · MEAN ${n.node.threatMean}/10`)
        }
        pending.push({
          rank: labelRank(isSelected, isHovered, R),
          sx, y: sy + R + 7, alpha: fade * (selected && !isSelected ? 0.32 : 1),
          colour, strong: isSelected || isHovered, lines,
        })
      }
    }
  }

  placeLabels(ctx, scene, guard, pending, 10.5)
}

// ── level 2: threads ─────────────────────────────────────────────────────────

function drawThreads(
  ctx: CanvasRenderingContext2D, scene: Scene, fade: number, guard: LabelGuard,
) {
  const { palette: P, layout: L, viewport: V, selected, hovered, showLabels, matches } = scene
  const k = zoomOf(V)
  const pending: PendingLabel[] = []

  for (const s of L.subjects) {
    for (const n of s.narratives) {
      const nx = px(V, n.x)
      const ny = py(V, n.y)
      const NR = Math.max(4, scaleRadius(V, n.r))
      if (nx + NR < -80 || nx - NR > scene.width + 80) continue
      if (ny + NR < -80 || ny - NR > scene.height + 80) continue

      const colour = narrativeInk(scene, n.node)
      ctx.save()
      ctx.globalAlpha = fade * 0.75
      ctx.strokeStyle = rgba(colour, 0.35)
      ctx.lineWidth = 1
      if (n.node.kind === 'COORDINATED') ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.arc(nx, ny, NR, 0, TAU)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.font = '400 9.5px ui-monospace, "JetBrains Mono", Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.fillStyle = rgba(colour, 0.9)
      ctx.fillText(clip(n.node.label.toUpperCase(), 46), nx, ny - NR - 6)
      ctx.restore()

      for (const t of n.threads) {
        const tx = px(V, t.x)
        const ty = py(V, t.y)
        if (tx < -30 || tx > scene.width + 30 || ty < -30 || ty > scene.height + 30) continue
        const R = Math.max(1.4, scaleRadius(V, t.r) * reachScale(scene, t.node.engagements))
        const isSelected = selected?.kind === 'thread' && selected.id === t.node.id
        const isHovered = hovered?.kind === 'thread' && hovered.id === t.node.id
        const c = threadInk(scene, t.node, n.node.kind === 'COORDINATED')
        // A search dims everything it did not match rather than hiding it, so the reader can
        // still see how much of the picture their query is actually describing.
        const missed = matches !== null && !matches.has(t.node.id)

        ctx.save()
        ctx.globalAlpha = fade * (missed ? 0.14 : selected && !isSelected ? 0.3 : 1)
        if (scene.encode === 'THREAT' && t.node.threat !== null
          && t.node.threat >= scene.threshold) {
          ctx.globalCompositeOperation = 'lighter'
          const halo = ctx.createRadialGradient(tx, ty, 0, tx, ty, R * 3.4)
          halo.addColorStop(0, rgba(c, 0.34))
          halo.addColorStop(1, rgba(c, 0))
          ctx.fillStyle = halo
          ctx.beginPath()
          ctx.arc(tx, ty, R * 3.4, 0, TAU)
          ctx.fill()
          ctx.globalCompositeOperation = 'source-over'
        }
        ctx.fillStyle = rgba(c, t.node.threat === null && scene.encode === 'THREAT' ? 0.42 : 0.92)
        ctx.beginPath()
        ctx.arc(tx, ty, R, 0, TAU)
        ctx.fill()
        // An unassessed thread is drawn hollow as well as grey. It has to be impossible to
        // mistake for a scored, low-threat one at a glance.
        if (t.node.threat === null && scene.encode === 'THREAT') {
          ctx.strokeStyle = rgba(P.unread, 0.85)
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(tx, ty, R + 1.2, 0, TAU)
          ctx.stroke()
        }
        // A deployed reply gets a ring: the ledger's fact, on the map.
        if (t.node.deployedCount > 0) {
          ctx.strokeStyle = rgba(P.supportive, 0.9)
          ctx.lineWidth = 1.2
          ctx.beginPath()
          ctx.arc(tx, ty, R + 3, 0, TAU)
          ctx.stroke()
        }
        if (isSelected || isHovered) {
          ctx.strokeStyle = rgba(P.accent, 0.85)
          ctx.lineWidth = 1
          ctx.setLineDash([2, 3])
          ctx.beginPath()
          ctx.arc(tx, ty, R + 6, 0, TAU)
          ctx.stroke()
          ctx.setLineDash([])
        }
        ctx.restore()

        if (showLabels && k >= 9 && !missed) {
          pending.push({
            rank: labelRank(isSelected, isHovered, R),
            sx: tx, y: ty + R + 5, alpha: fade, colour: c, strong: isSelected || isHovered,
            lines: [clip(t.node.title ?? t.node.authorHandle ?? 'untitled', 34)],
          })
        }
      }
    }
  }

  placeLabels(ctx, scene, guard, pending, 8.5)
}

// ── minimap ──────────────────────────────────────────────────────────────────

export interface MinimapRect { mx: number, my: number, mw: number, mh: number, s: number, ox: number, oy: number }

let lastMinimap: MinimapRect | null = null
export const minimapRect = () => lastMinimap

/**
 * Overview plus a draggable viewport box.
 *
 * At high zoom the screen shows a few per cent of the map, and without this there is no way to
 * know where in the corpus you are — which is the difference between a map and a microscope.
 */
function drawMinimap(ctx: CanvasRenderingContext2D, scene: Scene) {
  const { palette: P, layout: L, viewport: V, width: W, height: H } = scene
  const mw = 132
  const mh = 132
  const mx = W - mw - 58
  const my = H - mh - 18
  const s = mw / (L.ringR * 2.5)
  // World coordinates are centred on the origin, so the minimap anchors there too.
  const ox = mx + mw / 2 - L.cx * s
  const oy = my + mh / 2 - L.cy * s

  ctx.save()
  ctx.fillStyle = rgba(P.stage, 0.9)
  ctx.fillRect(mx, my, mw, mh)
  ctx.strokeStyle = rgba(P.accent, 0.26)
  ctx.lineWidth = 1
  ctx.strokeRect(mx, my, mw, mh)
  ctx.beginPath()
  ctx.rect(mx, my, mw, mh)
  ctx.clip()

  ctx.strokeStyle = rgba(P.accent, 0.2)
  ctx.beginPath()
  ctx.arc(ox + L.cx * s, oy + L.cy * s, L.ringR * s, 0, TAU)
  ctx.stroke()

  for (const subject of L.subjects) {
    ctx.fillStyle = rgba(subjectInk(scene, subject.node), 0.9)
    ctx.beginPath()
    ctx.arc(ox + subject.x * s, oy + subject.y * s,
      Math.max(1.2, subject.r * s * 0.5), 0, TAU)
    ctx.fill()
  }

  const vx = -V.x / V.k
  const vy = -V.y / V.k
  ctx.strokeStyle = rgba(P.accent, 0.85)
  ctx.lineWidth = 1
  ctx.strokeRect(ox + vx * s, oy + vy * s, (W / V.k) * s, (H / V.k) * s)
  ctx.restore()

  lastMinimap = { mx, my, mw, mh, s, ox, oy }
}

// ── hit testing ──────────────────────────────────────────────────────────────

/**
 * What is under the cursor, at the level currently in charge.
 *
 * Only the dominant level is tested. Hit-testing every level would let a click at thread zoom
 * land on the subject disc behind it, which is exactly the bug that makes a nested map feel
 * unpredictable.
 */
export function hitTest(scene: Scene, sx: number, sy: number): Hover | null {
  const { layout: L, viewport: V } = scene
  const alpha = levelAlpha(zoomOf(V))

  for (const p of L.platforms) {
    const d = Math.hypot(px(V, p.x) - sx, py(V, p.y) - sy)
    if (d <= p.r * V.fitK * Math.pow(zoomOf(V), 0.5) + 8) {
      return { kind: 'platform', id: p.node.code, sx, sy }
    }
  }

  if (alpha.THREAD >= 0.5) {
    let best: { hit: Hover, d: number } | null = null
    for (const s of L.subjects) {
      for (const n of s.narratives) {
        for (const t of n.threads) {
          const tx = px(V, t.x)
          const ty = py(V, t.y)
          if (tx < -20 || tx > scene.width + 20 || ty < -20 || ty > scene.height + 20) continue
          const d = Math.hypot(tx - sx, ty - sy)
          const r = Math.max(4, scaleRadius(V, t.r) + 3)
          if (d <= r && (!best || d < best.d)) {
            best = { hit: { kind: 'thread', id: t.node.id, sx, sy }, d }
          }
        }
      }
    }
    if (best) return best.hit
  }

  if (alpha.NARRATIVE >= 0.5 || alpha.THREAD >= 0.5) {
    for (const s of L.subjects) {
      for (const n of s.narratives) {
        const d = Math.hypot(px(V, n.x) - sx, py(V, n.y) - sy)
        if (d <= Math.max(6, scaleRadius(V, n.r)) + 4) {
          return { kind: 'narrative', id: n.node.id, sx, sy }
        }
      }
    }
  }

  for (const s of L.subjects) {
    const d = Math.hypot(px(V, s.x) - sx, py(V, s.y) - sy)
    if (d <= scaleRadius(V, s.r) + 4) {
      return { kind: 'subject', id: s.node.id, sx, sy }
    }
  }
  return null
}

export function findPoint(
  layout: Layout, sel: Selection,
): SubjectPoint | NarrativePoint | ThreadPoint | undefined {
  if (sel.kind === 'subject') return layout.subjectById.get(sel.id)
  if (sel.kind === 'narrative') return layout.narrativeById.get(sel.id)
  return layout.threadById.get(sel.id)
}

function clip(text: string, limit: number) {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text
}
